import { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { Chip, DrawerBodyWrapper, DrawerFooterWrapper, ItemCard, SectionHeader } from '@base'
import { DISPATCH_PLAN_STATUS, strings } from '@constants'
import { getDispatchPlansBySPO } from '@data/conn/get/dispatchPlan'
import { showLoader } from '@data/state/action'
import { capitalizeWord, displayAmount, displayWeight, showDateFormat } from '@utils'
import { toggleDrawer } from '@data/state/action/root'
import { paths } from '@routes'

const displayName = (user) => {
	return user?.organisation_name ?? user?.full_name ?? user?.organisation_id
}

const DispatchPlansBySPO = ({ orderId, poNumber, update }) => {
	const dispatch = useDispatch()
	const navigate = useNavigate()
	const [plansData, setPlansData] = useState([])

	useEffect(() => {
		dispatch(showLoader(true))
		getDispatchPlansBySPO(orderId, poNumber)
			.then((response) => {
				setPlansData(response.reverse())
			})
			.catch((error) => {})
			.finally(() => {
				dispatch(showLoader(false))
			})
	}, [orderId, poNumber])

	const basicData = useMemo(() => {
		let terms = plansData?.[0]?.payment_terms_list?.map((item, index) => (
			<div key={`${index}`}>
				{`${item?.percentage}% ${capitalizeWord(item?.payment_term ?? '')} ${
					item?.condition
						? item?.condition
								?.split('_')
								?.map((item) => capitalizeWord(item))
								?.join(' ')
						: ''
				} for ${item?.tenure_in_days} days`}
			</div>
		))
		return {
			poNumber: poNumber,
			orderId: orderId,
			buyer: displayName(plansData?.[0]?.buyer),
			paymentTerms: terms ?? '-',
			region: plansData?.[0]?.region ?? '-',
			supplyPerson: plansData?.[0]?.supply_person?.full_name ?? '-',
		}
	}, [plansData, poNumber, orderId])

	const renderBasicDetails = () => {
		const itemData = [
			{ label: strings('order_id'), value: basicData?.orderId },
			{ label: strings('buyer'), value: basicData?.buyer, wrap: true },
			{ label: strings(['supplier', 'po', 'number']), value: basicData?.poNumber },
			{
				label: strings('payment_terms'),
				value: basicData?.paymentTerms,
				wrap: true,
			},
			{ label: strings('region'), value: basicData?.region },
			{ label: strings('supply', 'person'), value: basicData?.supplyPerson },
		]
		return (
			<>
				<ItemCard data={itemData} medium />
			</>
		)
	}

	const onDispatchPlanClick = (dispatchPlanId) => {
		dispatch(toggleDrawer(false))
		navigate(paths.admin_manage_enquiry_order_edit_dispatch_plans_edit(orderId, dispatchPlanId))
	}

	const getStatusBg = (key) => {
		switch (key) {
			case DISPATCH_PLAN_STATUS.COMPLETED:
				return 'green'
			case DISPATCH_PLAN_STATUS.CANCELLED:
			case DISPATCH_PLAN_STATUS.DELETED:
				return '#c21625'
			case DISPATCH_PLAN_STATUS.ACTIVE:
				return '#1967D2'
			case DISPATCH_PLAN_STATUS.SCHEDULED:
				return '#ffa07a'
			case DISPATCH_PLAN_STATUS.RESCHEDULED:
				return '#e69500'
			case DISPATCH_PLAN_STATUS.CLOSED:
				return '#6b7280'
			default:
				return '#323234'
		}
	}
	const renderItemCards = () => {
		return (
			<>
				<SectionHeader>{`Dispatch Plans`}</SectionHeader>
				{plansData?.map((item, index) => {
					const itemData = [
						{
							label: strings('supplier'),
							value: displayName(item?.supplier) || '-',
							wrap: true,
						},
						{
							label: strings('start', 'date'),
							value: showDateFormat(new Date(item?.plan_start_date)) || '-',
						},
						{
							label: strings('current', 'date'),
							value: showDateFormat(new Date(item?.plan_current_date)) || '-',
							wrap: true,
						},
						{
							label: strings('status'),
							value: <Chip label={item?.status} status={'custom'} background={() => getStatusBg(item?.status)} color={'white'} />,
						},
						{ label: strings('logistics_handler'), value: item?.logistics_handler ?? '-' },
						{ label: strings('price_per_kg'), value: displayAmount(item?.price_per_kg) },
						{
							label: strings('planned', 'quantity'),
							value: displayWeight(item?.dispatch_quantity / 1000),
						},
						{
							label: strings('dispatched', 'quantity'),
							value: displayWeight(item?.serviced_quantity / 1000),
						},
						{
							label: strings('age'),
							value: item?.age,
						},
					]
					return (
						<ItemCard
							data={itemData}
							key={`plan-item-${index}`}
							medium
							clickable
							clickHandler={() => onDispatchPlanClick(item?.dispatch_plan_id)}
						/>
					)
				})}
			</>
		)
	}

	return (
		<>
			<DrawerBodyWrapper>
				{renderBasicDetails()}
				{renderItemCards()}
			</DrawerBodyWrapper>
			<DrawerFooterWrapper></DrawerFooterWrapper>
		</>
	)
}

export default DispatchPlansBySPO
